import type { components } from "@contracts/generated/editor-service";

export type LibraryAssetType = components["schemas"]["LibraryAssetType"];

/**
 * The library rail's eight sections, and how the contract's twelve asset types
 * fold into them.
 *
 * The rail is a navigation grouping, not a contract concept: `pathway` and
 * `pathway_variant` are one clinical family split only for adoption identity,
 * and the ticket names one "Safety paths/matrices" surface for `safety_track`
 * and `alert_matrix` together. The primary map is type → section so that
 * `satisfies` makes a THIRTEENTH asset type in a regenerated contract a compile
 * error here rather than an asset that silently appears in no section. The
 * twelfth arrived at #866 and is exactly what that guard caught.
 */

/**
 * `chief-complaint`, not `main-concern` (#492).
 *
 * The canonical library addresses this family at `/library/chief-complaint`, and
 * the clinical configuration rail has spelled the same family `chief-complaint`
 * all along (`shell/navModel.ts`'s `CLINICAL_CONFIG_SECTIONS`, matching the
 * contract's own `SectionSlug`). The library was the odd one out, so this rename
 * removes a second spelling rather than adding one. The LABEL is untouched — the
 * product still calls the family "Main concern".
 */
export const LIBRARY_SECTION_SLUGS = [
  "chief-complaint",
  "pathways",
  "rules",
  "safety-paths",
  "bg-sets",
  "questionnaires",
  // #866. Between Questionnaires and Summary, which is where the S7 screen
  // contract places the Closing conversation — and it is a group of its own
  // rather than a member of either, because the closing agent runs after every
  // final track and before the session ends, and is never a diagnostic
  // selection.
  "closing",
  "summary",
] as const;

export type LibrarySectionSlug = (typeof LIBRARY_SECTION_SLUGS)[number];

export const SECTION_BY_ASSET_TYPE = {
  main_concern: "chief-complaint",
  pathway: "pathways",
  pathway_variant: "pathways",
  language_rules: "rules",
  safety_track: "safety-paths",
  alert_matrix: "safety-paths",
  background_set: "bg-sets",
  // #858's shared interview profile shares the BG rail section with the
  // domains: the Lovable reference groups "Set files" and "Domains" on ONE
  // `/library/bg-sets` screen, and the rail is a navigation grouping rather
  // than a contract concept.
  background_profile: "bg-sets",
  questionnaire: "questionnaires",
  summary_prompt: "summary",
  closing_conversation: "closing",
  speech_profile: "rules",
} as const satisfies Record<LibraryAssetType, LibrarySectionSlug>;

export const ASSET_TYPE_LABELS = {
  main_concern: "Main concern",
  pathway: "Pathway",
  pathway_variant: "Pathway variant",
  language_rules: "Language rules",
  safety_track: "Safety track",
  alert_matrix: "Alert matrix",
  background_set: "BG set",
  background_profile: "BG interview profile",
  questionnaire: "Questionnaire",
  summary_prompt: "Summary prompt",
  closing_conversation: "Closing conversation",
  speech_profile: "Speech profile",
} as const satisfies Record<LibraryAssetType, string>;

/**
 * The same families, named as a GROUP rather than as one asset (#908).
 *
 * A catalog that separates its section into groups heads each with the kind of
 * asset it holds — "Safety tracks", "Alert matrices" — and `ASSET_TYPE_LABELS`
 * above names ONE ("Safety track"). English plurals are not derivable from the
 * singular ("matrix" is the standing counter-example), so they are declared
 * rather than computed, and `satisfies` keeps this table exhaustive over the
 * contract's own asset types exactly as the singular one is.
 */
export const ASSET_TYPE_GROUP_LABELS = {
  main_concern: "Main concerns",
  pathway: "Pathways",
  pathway_variant: "Pathway variants",
  language_rules: "Language rules",
  safety_track: "Safety tracks",
  alert_matrix: "Alert matrices",
  background_set: "BG sets",
  background_profile: "BG interview profiles",
  questionnaire: "Questionnaires",
  summary_prompt: "Summary prompts",
  closing_conversation: "Closing conversations",
  speech_profile: "Speech profiles",
} as const satisfies Record<LibraryAssetType, string>;

export function isLibrarySectionSlug(
  value: string,
): value is LibrarySectionSlug {
  return (LIBRARY_SECTION_SLUGS as readonly string[]).includes(value);
}

export function assetTypesForSection(
  section: LibrarySectionSlug,
): LibraryAssetType[] {
  return (
    Object.keys(SECTION_BY_ASSET_TYPE) as LibraryAssetType[]
  ).filter((assetType) => SECTION_BY_ASSET_TYPE[assetType] === section);
}

export function sectionForAssetType(
  assetType: LibraryAssetType,
): LibrarySectionSlug {
  return SECTION_BY_ASSET_TYPE[assetType];
}

/**
 * The canonical library address of one section's catalog (#615).
 *
 * Never bare `/library`: that path is a redirect to whichever section the
 * router names first, so a control sending a clinician "to the library" from a
 * family-specific surface would land them on an unrelated family's catalog.
 */
export function librarySectionHref(section: LibrarySectionSlug): string {
  return `/library/${section}`;
}

/**
 * The canonical library address of one exact asset (#615).
 *
 * The ONE derivation of an asset's library page from what the adoption/catalog
 * contract states — its `assetType` and `id` — so every surface that offers
 * `View in Clinical library` computes the same address. There is deliberately
 * no overload taking a name, a label, or a section a row happens to render in:
 * those are the identity inferences the ratified contract rules out.
 */
export function libraryAssetHref(
  assetType: LibraryAssetType,
  assetId: string,
): string {
  return `/library/${SECTION_BY_ASSET_TYPE[assetType]}/${encodeURIComponent(assetId)}`;
}
